import { Badge } from "@component/badge";
import { EmptyState } from "@component/empty-state";
import { Input } from "@component/input";
import LoadingBox from "@component/loading-box";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@component/table";
import { defaultPagination } from "@constant/app";
import { getUserReferralJobs } from "@service/payment";
import { ReferralItem } from "@type/payment";
import { convertCurrency } from "@util/currency";
import { parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { getBonusStatusColor } from "@util/string";
import { GiftIcon, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";

export default function CandidateListReferral() {
    const [selectedStatus, setSelectStatus] = useState<string>("");
    const [referrals, setReferral] = useState<ReferralItem[]>([]);
    const [keyword, setKeyword] = useState<string>("");
    const [isLoading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        onSearch("", selectedStatus);
    }, []);

    const onSearch = (keyword: string, status: string) => {
        getUserReferralJobs({
            page: defaultPagination.page,
            limit: defaultPagination.limit,
            keyword: keyword,
            filter_status: status === "all" ? "" : status
        }).then((resp) => {
            setReferral(resp.data.data.items || []);
        }).catch((error) => {
            catchErrorLog(`failed to fetch user referral jobs ${error}`);
        }).finally(() => {
            setLoading(false);
        });
    }

    const handleSearchChange = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSearch(keyword, selectedStatus);
    }

    const handleOnChangeSearch = (keyword: string) => {
        if (keyword === "") {
            onSearch("", selectedStatus);
        }
    }

    const handleStatusChange = (status: string) => {
        setSelectStatus(status);
        onSearch(keyword, status);
    }

    return (
        <>
            <div className="flex flex-col sm:flex-row gap-3 mb-4 w-full">
                <form onSubmit={handleSearchChange} className="relative flex-grow">
                    <div>
                        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search candidates by name or role..."
                            className="pl-9 w-full"
                            onChange={(e) => {
                                setKeyword(e.target.value);
                                handleOnChangeSearch(e.target.value);
                            }}
                        />
                    </div>
                </form>
                <Select disabled={isLoading} value={selectedStatus} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="Pending Payment">Pending Payment</SelectItem>
                        <SelectItem value="Paid">Paid</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Ineligible">Ineligible</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="w-full overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[250px]">Candidate</TableHead>
                                <TableHead>Position</TableHead>
                                <TableHead>Applied Date</TableHead>
                                <TableHead>Bonus</TableHead>
                                <TableHead className="text-center">Payment Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <LoadingBox />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {referrals.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <EmptyState
                                                    icon={<GiftIcon className="h-6 w-6 text-blue-600" />}
                                                    title="Earn money by referring friends."
                                                    description="Invite your friends to join our platform and earn rewards when they get hired. Share your unique referral link to start earning."
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {referrals.map((referral, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <div className="overflow-hidden w-9 h-9 rounded-full">
                                                        <img
                                                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${referral.user_avatar}`}
                                                            onError={(e) => {
                                                                e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{referral.user_fullname}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="text-sm font-medium">{referral.position}</p>
                                                    {/* <p className="text-xs text-gray-500">
                                                {referral.company_name}
                                            </p> */}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm">{parseFormat(referral.applied_date, "D MMM YYYY")}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm font-medium">
                                                    {convertCurrency(referral.referral_amount, referral.referral_currency)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="outline" className={`${getBonusStatusColor(referral.status)} capitalize mx-auto`}>
                                                    {referral.status}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    )
}